import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Globe } from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';
import { useAdminBasePath } from '../../hooks/useAdminBasePath';

type FirecrawlConfig = {
  base_url?: string;
  source?: string;
};

export const FirecrawlConfigPage: React.FC = () => {
  const base = useAdminBasePath();
  const isEnterprise = base.includes('/app');

  const [form, setForm] = useState<FirecrawlConfig>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = (await fastapiService.getFirecrawlConfig()) as { data?: FirecrawlConfig };
      setForm(res?.data || {});
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.base_url?.trim()) {
      setError('Base URL is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setMessage(null);
      await fastapiService.updateFirecrawlConfig({ base_url: form.base_url.trim() });
      setMessage('Firecrawl URL saved. Knowledge Hub will use this endpoint for website discovery and extraction.');
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const titleClass = isEnterprise ? 'text-2xl font-bold text-enterprise-900' : 'text-2xl font-bold text-admin-900';
  const labelClass = isEnterprise ? 'text-sm font-medium text-enterprise-800' : 'text-sm font-medium text-admin-800';
  const inputClass = isEnterprise
    ? 'mt-1 w-full rounded-lg border border-enterprise-200 px-3 py-2 text-sm font-mono'
    : 'mt-1 w-full rounded-lg border border-admin-200 px-3 py-2 text-sm font-mono';

  const sourceLabel =
    form.source === 'platform_settings'
      ? 'Saved in platform settings'
      : form.source === 'env'
        ? 'From FIRECRAWL_URL environment variable (not yet overridden in admin)'
        : 'Built-in default';

  return (
    <div className="max-w-2xl">
      <Link
        to={`${base}/settings`}
        className="mb-6 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to settings
      </Link>

      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
          <Globe className="h-5 w-5" />
        </div>
        <div>
          <h1 className={titleClass}>Firecrawl API</h1>
          <p className="mt-1 text-sm text-gray-600">
            Base URL for Knowledge Hub website discovery and page extraction. Super admins can change this without redeploying.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}
      {message && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {message}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <form onSubmit={handleSave} className="space-y-5 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div>
            <label className={labelClass}>Firecrawl base URL</label>
            <input
              className={inputClass}
              value={form.base_url || ''}
              onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
              placeholder="http://206.189.139.9:4002"
              spellCheck={false}
            />
            <p className="mt-1 text-xs text-gray-500">
              Origin only — no trailing path. Used for <code className="rounded bg-gray-100 px-1">/v1/map</code> and{' '}
              <code className="rounded bg-gray-100 px-1">/v1/scrape</code>.
            </p>
            <p className="mt-2 text-xs text-gray-500">Currently active source: {sourceLabel}</p>
          </div>
          <button
            type="submit"
            disabled={saving}
            className={
              isEnterprise
                ? 'rounded-lg bg-enterprise-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50'
                : 'rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50'
            }
          >
            {saving ? 'Saving…' : 'Save Firecrawl URL'}
          </button>
        </form>
      )}
    </div>
  );
};
